import { useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useCampaignsManager } from '../hooks/useCampaignsManager';
import { Outlet } from 'react-router-dom';
import { useAppDispatch } from '../hooks/useAppDipatch';
import { useAppSelector } from '../hooks/useAppSelector';
import { setCreationStep, setShowLaunchConfirmed } from '../features/campaigns/campaignsSlice';
import { getCampaignObjective, fetchPricingConfig } from '../features/campaigns/campaignsThunk';
import MarketFormData from '../components/campaigns/Ad/CreateMarketForm';
import AdCreationForm from '../components/campaigns/Ad/CreateAdForm';
import CreateCampaignForm from '../components/campaigns/CreateCampaignForm';
import CampaignStepper from '../components/campaigns/CampaignStepper';
import LaunchConfirmedModal from '../components/campaigns/LaunchConfirmedModal';
import BillingRequiredModal from '../components/campaigns/BillingRequiredModal';
import { CampaignCreationStep } from '../types';
import { BudgetPreviewProvider } from '../contexts/BudgetPreviewContext';
import ProjectedImpactSidebar from '../components/campaigns/ProjectedImpactSidebar';

export const CampaignLayout = () => {
  const dispatch = useAppDispatch();
  const { campaign, creationStep, showLaunchConfirmed, campaignObjective } = useAppSelector((state) => state.campaigns);
  const { isMarketScreen } = useAppSelector((state) => state.markets);
  const {
    pendingCampaign,
    pendingMarket,
    showBillingModal,
    handleCampaignSubmit,
    handleMarketSubmit,
    handleAdSubmit,
    closeBillingModal,
  } = useCampaignsManager();

  // Fetch campaign objectives when entering creation mode
  // This ensures data is available even when navigating directly to campaign form
  useEffect(() => {
    if (creationStep === 'campaign' && campaignObjective.length === 0) {
      dispatch(getCampaignObjective());
      dispatch(fetchPricingConfig());
    }
  }, [creationStep, campaignObjective.length, dispatch]);

  const renderOtherSteps = () => {
    switch (creationStep as CampaignCreationStep) {
      case 'markets':
        return (
          <MarketFormData
            defaultValues={pendingMarket}
            campaign={campaign}
            onSubmit={handleMarketSubmit}
          />
        );
      case 'offer':
        return (
          <AdCreationForm defaultValues={null} campaign={campaign} onSubmit={handleAdSubmit} />
        );
      default:
        return <Outlet />;
    }
  };

  const showStepper = creationStep === 'campaign' || creationStep === 'markets';
  const isCampaignStep = creationStep === 'campaign';

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-neutral-light to-text-muted text-transparent bg-clip-text">
            Campaign Management
          </h1>
          {creationStep === 'none' && !isMarketScreen && (
            <button
              onClick={() => dispatch(setCreationStep('campaign'))}
              className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-hover text-brand-bg font-medium rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Create Campaign</span>
            </button>
          )}
        </div>
        {showStepper && <CampaignStepper currentStep={creationStep} />}

        {isCampaignStep ? (
          <BudgetPreviewProvider>
            <div className="flex flex-col xl:flex-row gap-6">
              <div className="flex-1 min-w-0">
                <CreateCampaignForm
                  defaultValues={pendingCampaign}
                  onSubmit={handleCampaignSubmit}
                />
              </div>
              <div className="w-full xl:w-[340px] flex-shrink-0">
                <ProjectedImpactSidebar />
              </div>
            </div>
          </BudgetPreviewProvider>
        ) : (
          renderOtherSteps()
        )}
      </div>

      <LaunchConfirmedModal
        open={showLaunchConfirmed}
        onClose={() => dispatch(setShowLaunchConfirmed(false))}
        campaignName={campaign?.name || ''}
      />

      <BillingRequiredModal
        open={showBillingModal}
        onClose={closeBillingModal}
      />
    </>
  );
};
